import classNames from 'classnames';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
} from 'react';
import { Tab } from './Tab/Tab';
import { TabList } from './TabList/TabList';
import { TabPanel } from './TabPanel/TabPanel';
import { TabPanels } from './TabPanels/TabPanels';

import './Tabs.scss';

type TabsContext = {
  activeIndex: number;
  id: string;
  onChange?: (args: { index: number }) => void;
  onSelectTab: (tabId: number) => void;
};

const Context = createContext<TabsContext | undefined>(undefined);

type TabsProps = {
  className?: string;
  defaultActiveIndex?: number;
  id?: string;
  onChange?: (args: { index: number }) => void;
};

export function Tabs({
  id = 'tabs',
  children,
  className,
  defaultActiveIndex = 0,
  onChange,
}: PropsWithChildren<TabsProps>) {
  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);
  const tabClassName = classNames('Tabs', className);

  return (
    <Context.Provider
      value={{
        activeIndex,
        id,
        onChange,
        onSelectTab: setActiveIndex,
      }}
    >
      <div data-testid="tabs" className={tabClassName}>
        {children}
      </div>
    </Context.Provider>
  );
}

export const useTabsContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error('Please use useTabsContext within a Tabs provider');
  }
  return context;
};

Tabs.Tab = Tab;
Tabs.TabList = TabList;
Tabs.TabPanel = TabPanel;
Tabs.TabPanels = TabPanels;
